/**
 * dateTimeFormatting mixin
 *
 * 日時の表示フォーマット・パースを集約する。`$i18n.locale` 連動が
 * 必要な処理は本 mixin を経由する。Vue コンポーネントから `mixins:
 * [dateTimeFormatting]` で導入し、`this.formatDate(value)` のように
 * 使う。
 *
 * ## 適用範囲
 *
 * | 種別                                      | 使うもの             |
 * |-------------------------------------------|----------------------|
 * | 表示用フォーマット                        | 本 mixin             |
 * | API 送信用フォーマット (yyyy-MM-dd)       | 本 mixin             |
 * | パース (固定フォーマット文字列)           | 本 mixin             |
 * | 演算 (add/sub, startOf/endOf, daysInMonth)| date-fns 直 import   |
 * | 比較 (isAfter/isBefore/isSame)            | date-fns 直 import   |
 * | 差分 (differenceIn*)                      | date-fns 直 import   |
 * | Unix 変換 (fromUnixTime / getUnixTime)    | date-fns 直 import   |
 *
 * 表示・パースが本 mixin に集約される理由は `$i18n.locale` への連動。
 * 演算・比較は i18n 非依存のためラップ不要。
 *
 * ## 入力値
 *
 * 各メソッドは `null` / `Date` / `number`（Unix 秒）/ `string`（ISO）を受ける。
 * `null` または falsy の場合は空文字を返す（`formatTimestamp` のみ
 * `i18n` の `timestamp_none` を返す）。
 *
 * ## 拡張方針
 *
 * - 表示用フォーマット・パースのメソッドのみ本 mixin に追加する
 * - 演算・比較・差分・Unix 変換は date-fns を直 import で扱う
 *
 * ## 関連
 *
 * - 親チケット: Redmine #99574
 * - 関連ルール: `.claude/docs/rules/rule-date-library.md`
 */

import { format, fromUnixTime, parse } from 'date-fns';
import VueI18n from 'vue-i18n';

const i18n = new VueI18n({
  locale: 'en',
  messages: {
    en: {
      timestamp_format: 'yyyy/MM/dd HH:mm',
      timestamp_none: '--',
    },
    ja: {
      timestamp_format: 'yyyy/MM/dd HH:mm',
      timestamp_none: '--',
    },
  },
});

// フォーマット定数
const DATE_FORMAT = 'yyyy/MM/dd';
const DATETIME_FORMAT = 'yyyy/MM/dd HH:mm';
const DATETIME_WITH_SECONDS_FORMAT = 'yyyy/MM/dd HH:mm:ss';
const TIME_FORMAT = 'HH:mm';
const MONTH_FORMAT = 'yyyy-MM';
const YEAR_MONTH_FORMAT = 'yyyy/MM';
const DATE_API_FORMAT = 'yyyy-MM-dd';

// 'yyyy-MM' / 'yyyy-MM-dd' / 'yyyy-MM-dd HH:mm' 形式の文字列はローカル時刻として解釈する。
// new Date(string) は ECMA 仕様により UTC として解釈されるため、ローカル TZ で月境界・日境界が
// 1 日 (≒ 1 ヶ月) ずれる事故を防ぐ。
const STRING_PATTERNS = [
  { regex: /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/, format: 'yyyy-MM-dd HH:mm' },
  { regex: /^\d{4}-\d{2}-\d{2}$/, format: 'yyyy-MM-dd' },
  { regex: /^\d{4}-\d{2}$/, format: 'yyyy-MM' },
];

// 入力値をDateオブジェクトに変換
// NOTE: 型ごとに分岐する暫定実装。string の fallback は new Date() の UTC 罠が残る。
// 型別関数への分割（恒久対応）は #111445。
const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'number') return fromUnixTime(value);
  if (typeof value === 'string') {
    const matched = STRING_PATTERNS.find((p) => p.regex.test(value));
    if (matched) return parse(value, matched.format, new Date());
    return new Date(value);
  }
  return null;
};

/**
 * 日付を `yyyy/MM/dd` で整形する純粋関数。`this` 非依存のため Vue 以外
 * （pure JS）からも import して使える。mixin の formatDate はこれを呼ぶ。
 * @param {Date|number|string|null} value
 * @returns {string} value が falsy なら空文字
 */
export const formatDateStr = (value) => {
  const date = toDate(value);
  if (!date) return '';
  return format(date, DATE_FORMAT);
};

/**
 * タイムスタンプを `yyyy/MM/dd HH:mm` で整形する純粋関数。
 * @param {Date|number|string|null} value
 * @returns {string} value が falsy なら空文字
 */
export const formatDateTime = (value) => {
  const date = toDate(value);
  if (!date) return '';
  return format(date, DATETIME_FORMAT);
};

/**
 * タイムスタンプを `yyyy/MM/dd HH:mm:ss` で整形する純粋関数。
 * @param {Date|number|string|null} value
 * @returns {string} value が falsy なら空文字
 */
export const formatDateTimeWithSecondsStr = (value) => {
  const date = toDate(value);
  if (!date) return '';
  return format(date, DATETIME_WITH_SECONDS_FORMAT);
};

export default {
  computed: {
    /**
     * `$i18n.locale` に連動するタイムスタンプ表示。
     * 言語切替時にリアクティブに再計算される。
     * @returns {(timestamp: Date|number|string|null) => string}
     */
    formatTimestamp() {
      return (timestamp) => {
        if (!timestamp) {
          return i18n.t('timestamp_none', this.$i18n.locale);
        }
        const formatString = i18n.t('timestamp_format', this.$i18n.locale);
        return format(toDate(timestamp), formatString);
      };
    },
  },
  methods: {
    /**
     * 日付を `yyyy/MM/dd` で表示。
     * @example formatDate(new Date('2026-04-21')) // '2026/04/21'
     * @param {Date|number|string|null} value
     * @returns {string} `value` が falsy なら空文字
     */
    formatDate(value) {
      return formatDateStr(value);
    },
    /**
     * タイムスタンプを `yyyy/MM/dd HH:mm` で表示。
     * @example formatDateTime(new Date('2026-04-21T14:30')) // '2026/04/21 14:30'
     * @param {Date|number|string|null} value
     * @returns {string}
     */
    formatDateTime(value) {
      const date = toDate(value);
      if (!date) return '';
      return format(date, DATETIME_FORMAT);
    },
    /**
     * タイムスタンプを `yyyy/MM/dd HH:mm:ss` で表示。
     * @example formatDateTimeWithSeconds(new Date('2026-04-21T14:30:45')) // '2026/04/21 14:30:45'
     * @param {Date|number|string|null} value
     * @returns {string}
     */
    formatDateTimeWithSeconds(value) {
      return formatDateTimeWithSecondsStr(value);
    },
    /**
     * 時刻のみを `HH:mm` で表示。
     * @example formatTime(new Date('2026-04-21T14:30')) // '14:30'
     * @param {Date|number|string|null} value
     * @returns {string}
     */
    formatTime(value) {
      const date = toDate(value);
      if (!date) return '';
      return format(date, TIME_FORMAT);
    },
    /**
     * `yyyy-MM` 文字列を整形。月ラベル用。
     * @example formatMonth('2026-04') // '2026-04'
     * @param {string|null} monthString
     * @returns {string}
     */
    formatMonth(monthString) {
      if (!monthString) return '';
      const date = parse(monthString, 'yyyy-MM', new Date());
      return format(date, MONTH_FORMAT);
    },
    /**
     * 日付を `yyyy-MM-dd` で表示。API/クエリパラメータ用。
     * @example formatDateForApi(new Date('2026-04-21')) // '2026-04-21'
     * @param {Date|number|string|null} value
     * @returns {string}
     */
    formatDateForApi(value) {
      const date = toDate(value);
      if (!date) return '';
      return format(date, DATE_API_FORMAT);
    },
    /**
     * 年月を `yyyy/MM` で表示。
     * @example formatYearMonth(new Date('2026-04-21')) // '2026/04'
     * @param {Date|number|string|null} value
     * @returns {string}
     */
    formatYearMonth(value) {
      const date = toDate(value);
      if (!date) return '';
      return format(date, YEAR_MONTH_FORMAT);
    },
    /**
     * 年月を `yyyy-MM` で表示。API/クエリパラメータ用。
     * @example formatMonthForApi(new Date('2026-04-21')) // '2026-04'
     * @param {Date|number|string|null} value
     * @returns {string}
     */
    formatMonthForApi(value) {
      const date = toDate(value);
      if (!date) return '';
      return format(date, MONTH_FORMAT);
    },
    /**
     * i18n キーから取得した動的フォーマット文字列で日付を整形する。
     * フォーマット文字列は date-fns 書式（`yyyy/MM/dd` 等）であること。
     *
     * @param {Date|number|string|null} value 'yyyy-MM-dd' / 'yyyy-MM' / 'yyyy-MM-dd HH:mm' 文字列はローカル時刻として解釈
     * @param {string} formatString date-fns 書式
     * @returns {string} 整形済み文字列。value が falsy の場合は空文字
     */
    formatLocalized(value, formatString) {
      const date = toDate(value);
      if (!date) return '';
      return format(date, formatString);
    },
  },
};
